import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { buildClassName } from 'utils/build-class-name';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_CALC_AI_INSIGHT_CLICK } from 'constants/events';
import cardStyles from './ui/card.module.scss';
import styles from './AIInsights.module.scss';

interface AIInsightsProps {
  homePrice: number;
  downPayment: number;
  interestRate: number;
  amortization: number;
  monthlyPayment: number;
  totalInterest: number;
  cmhcInsurance: number;
}

const AIInsights = ({
  homePrice,
  downPayment,
  interestRate,
  amortization,
  monthlyPayment: _monthlyPayment,
  totalInterest,
  cmhcInsurance,
}: AIInsightsProps) => {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<string[]>([]);

  const generateInsights = async () => {
    AnalyticsController.trackEvent(GTM_CALC_AI_INSIGHT_CLICK, 'Mortgage Calculator');
    setLoading(true);
    try {
      const newInsights: string[] = [];

      // Down payment insight
      const downPaymentPercent = (downPayment / homePrice) * 100;
      if (downPaymentPercent < 20 && cmhcInsurance > 0) {
        const savingsWithoutCMHC = cmhcInsurance;
        const additionalDownPayment = homePrice * 0.2 - downPayment;
        newInsights.push(
          `💡 **Avoid CMHC Insurance:** By increasing your down payment by $${additionalDownPayment.toLocaleString()}, you could save $${savingsWithoutCMHC.toLocaleString()} in CMHC insurance.`
        );
      }

      // Amortization insight
      if (amortization > 20) {
        const shorterAmortization = 20;
        const estimatedSavings = totalInterest * 0.2;
        newInsights.push(
          `📉 **Reduce Your Amortization:** Shortening your amortization to ${shorterAmortization} years could save you approximately $${estimatedSavings.toLocaleString()} in interest, though your monthly payment would increase.`
        );
      }

      // Payment frequency insight
      newInsights.push(
        '⚡ **Accelerated Bi-weekly Payments:** Switching to accelerated bi-weekly payments can help you pay off your mortgage faster and save thousands in interest over the life of your loan.'
      );

      // Interest rate insight
      if (interestRate > 4.5) {
        newInsights.push(
          `🔍 **Shop Around for Rates:** Your current rate of ${interestRate.toFixed(2)}% is on the higher end. Consider shopping around with different lenders or working with a mortgage broker to find better rates.`
        );
      }

      setInsights(newInsights);
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={buildClassName(cardStyles.shadowElevated, styles.card)}>
      <CardHeader className={styles.header}>
        <CardTitle className={styles.title}>
          <span className={styles.icon}>✨</span>
          AI Mortgage Coach
        </CardTitle>
      </CardHeader>
      <CardContent className={styles.content}>
        {insights.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>💡</span>
            <p className={styles.emptyText}>
              Get personalized insights to optimize your mortgage and save money
            </p>
            <Button onClick={generateInsights} disabled={loading} className={styles.button}>
              {loading ? 'Generating Insights...' : 'Get AI Insights'}
            </Button>
          </div>
        ) : (
          <div className={styles.insightsList}>
            {insights.map((insight, index) => (
              <div
                key={index}
                className={styles.insightItem}
              >
                <p className={styles.insightText}>{insight}</p>
              </div>
            ))}
            <Button
              onClick={generateInsights}
              disabled={loading}
              variant="outline"
              className={styles.refreshButton}
            >
              <span className={styles.refreshIcon}>📊</span>
              Refresh Insights
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIInsights;
